import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Eye } from "lucide-react";
import { getTemplate } from "@/components/churchbuilder/templates";

// Inject <head> tags for the church's SEO + tracking config.
const applySeoTags = (c) => {
  if (!c) return;
  const apiHost = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const resolve = (u) => (u && u.startsWith("/api/")) ? `${apiHost}${u}` : u;

  if (c.meta_title || c.name) {
    document.title = c.meta_title || `${c.name} · Welcome`;
  }
  let metaDesc = document.querySelector('meta[name="description"]');
  if (!metaDesc) {
    metaDesc = document.createElement("meta");
    metaDesc.setAttribute("name", "description");
    document.head.appendChild(metaDesc);
  }
  metaDesc.setAttribute("content", c.meta_description || `Welcome to ${c.name || "our church"}.`);

  const ogImg = resolve(c.og_image_url || c.hero_image_url || c.logo_url);
  const setMeta = (prop, val, attr = "property") => {
    if (!val) return;
    let el = document.querySelector(`meta[${attr}="${prop}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, prop);
      document.head.appendChild(el);
    }
    el.setAttribute("content", val);
  };
  setMeta("og:title", c.meta_title || c.name);
  setMeta("og:description", c.meta_description || `Welcome to ${c.name || "our church"}.`);
  if (ogImg) setMeta("og:image", ogImg);
  setMeta("og:type", "website");
  setMeta("twitter:card", "summary_large_image", "name");
  if (ogImg) setMeta("twitter:image", ogImg, "name");

  const fav = resolve(c.favicon_url);
  if (fav) {
    let link = document.querySelector('link[rel~="icon"][data-church-favicon]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      link.setAttribute("data-church-favicon", "1");
      document.head.appendChild(link);
    }
    link.href = fav;
  }

  if (c.google_analytics_id && !document.querySelector(`script[data-ga-id="${c.google_analytics_id}"]`)) {
    const s1 = document.createElement("script");
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${c.google_analytics_id}`;
    s1.dataset.gaId = c.google_analytics_id;
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.dataset.gaInit = "1";
    s2.text = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${c.google_analytics_id}');`;
    document.head.appendChild(s2);
  }

  if (c.facebook_pixel_id && !document.querySelector(`script[data-fbq-id="${c.facebook_pixel_id}"]`)) {
    const s = document.createElement("script");
    s.dataset.fbqId = c.facebook_pixel_id;
    s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${c.facebook_pixel_id}');fbq('track','PageView');`;
    document.head.appendChild(s);
  }
};

export default function PublicChurchSite() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const isPreview = params.get("preview") === "1";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = isPreview ? `/church-builder/preview/${slug}` : `/churches/${slug}`;
    api.get(url)
      .then((r) => {
        setData(r.data);
        applySeoTags(r.data?.church);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Site not found"));
    return () => {
      document.title = "HOH_Vibes | Music, Power BI Templates & Digital Marketplace";
      // Clean up church-specific head tags so switching churches in the SPA
      // doesn't leak the previous church's analytics / favicon / OG tags.
      document.querySelectorAll('link[rel~="icon"][data-church-favicon]').forEach((el) => el.remove());
      document.querySelectorAll('script[data-ga-id], script[data-ga-init], script[data-fbq-id]').forEach((el) => el.remove());
    };
  }, [slug, isPreview]);

  if (error) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="public-church-error">
      <div className="text-center max-w-md">
        <div className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-3">404</div>
        <h1 className="font-serif-display text-4xl">Site not available</h1>
        <p className="text-neutral-400 mt-3">{error}</p>
        <a href="/church-builder" className="text-[#D4AF37] mt-6 inline-block text-sm hover:underline">Build your own →</a>
      </div>
    </div>
  );

  if (!data) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;

  const tplEntry = getTemplate(data.church.template_id);
  const TemplateComponent = tplEntry.component;
  return (
    <>
      {isPreview && (
        <div data-testid="public-preview-banner" className="bg-[#D4AF37] text-black text-xs tracking-widest uppercase px-4 py-2 flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 font-semibold"><Eye size={13} /> Preview mode · this site is not published</span>
          <Link to={`/church-builder/${slug}/dashboard`} className="underline">Back to dashboard</Link>
        </div>
      )}
      <TemplateComponent church={data.church} template={data.template} />
    </>
  );
}
